import type { StudyMode } from "@rmnlng/shared";
import { CapstoneStar } from "@/components/skills/atoms";
import { useMe, useUpdateMe } from "@/lib/auth";
import { features } from "@/lib/features";
import { useT } from "@/lib/i18n";
import { countMastered, useScenarios } from "@/lib/scenarios";
import { cn } from "@/lib/utils";

/**
 * 3-segment mode switcher (Open / Level / Skills) — the header control
 * on Home. Pressing a segment writes `studyMode` via PATCH /me, the
 * mutation invalidates the queue cache, and Home re-dispatches its
 * body. Optimistic via `useUpdateMe.variables` so the click feels
 * instant.
 *
 * Mockup: `design/hifi/scenarios.jsx` `ModeSwitch3` lines ~187–222.
 *
 * Each segment shows two lines:
 *   - top:    big mono identifier (Open / A1 / 14)
 *   - bottom: tiny uppercase mode name (Free / Level / Skills)
 *
 * The big number for Skills = scenarios mastered by the user. We
 * fetch this only when `enabled` (i.e. the user already has a token);
 * pre-login renders 0 quietly.
 */
export function ModeSwitch3() {
  const { data: user } = useMe();
  const update = useUpdateMe();
  const scenarios = useScenarios();
  const t = useT();

  const current: StudyMode = update.variables?.studyMode ?? user?.studyMode ?? "LEVEL";
  const currentLevel = user?.currentLevel ?? "A0";
  const mastered = countMastered(scenarios.data);
  function pick(next: StudyMode) {
    if (next === current) return;
    update.mutate({ studyMode: next });
  }

  const allSegments: Array<{
    id: StudyMode;
    label: string;
    sub: string;
    activeColor: string;
    showStar?: boolean;
  }> = [
    {
      id: "OPEN",
      label: t("mode_switch.label_open"),
      sub: t("mode_switch.sub_open"),
      activeColor: "var(--accent)",
    },
    {
      id: "LEVEL",
      label: currentLevel,
      sub: t("mode_switch.sub_level"),
      activeColor: "var(--accent)",
    },
    {
      id: "SCENARIOS",
      label: String(mastered),
      sub: t("mode_switch.sub_skills"),
      activeColor: "var(--scen-solid)",
      showStar: true,
    },
  ];
  const segments = allSegments.filter((s) => features.skillsEnabled || s.id !== "SCENARIOS");

  return (
    <div
      className="inline-flex shrink-0 rounded-[10px] border border-border bg-[color:var(--surface-2)] p-[3px]"
      role="tablist"
      aria-label="study mode"
    >
      {segments.map((s) => {
        const on = s.id === current;
        return (
          <button
            key={s.id}
            type="button"
            role="tab"
            aria-selected={on}
            onClick={() => pick(s.id)}
            disabled={update.isPending && update.variables?.studyMode === s.id}
            className={cn(
              "flex min-w-[50px] flex-col items-center justify-center rounded-[7px] px-[11px] py-[5px] transition-colors",
              on ? "bg-surface shadow-[var(--shadow-sm)]" : "hover:bg-surface/50",
            )}
          >
            <span
              className="inline-flex items-center gap-[3px] font-mono text-[12px] font-bold leading-[1.2]"
              style={{ color: on ? s.activeColor : "var(--text-3)" }}
            >
              {s.showStar && (
                <span style={{ color: on ? "var(--scen-solid)" : "var(--text-3)" }}>
                  <CapstoneStar size={9} />
                </span>
              )}
              {s.label}
            </span>
            <span
              className="mt-px text-[9px] font-medium uppercase tracking-[0.08em] leading-[1.2]"
              style={{ color: on ? "var(--text-2)" : "var(--text-3)" }}
            >
              {s.sub}
            </span>
          </button>
        );
      })}
    </div>
  );
}
